import type { CSSProperties } from 'react';
import { TRACK_TARGET_POSITION_PERCENT } from '../../constants/overviewConstants';
import styles from './TargetTrack.module.css';

interface RealisedMarker {
  percent: number;
  label: string;
}

interface TargetTrackProps {
  lockedPercent: number;
  openPercent?: number;
  excessPercent?: number;
  targetLabel: string;
  // Absent before anything has landed, when a caret at zero would read as a
  // measurement rather than an absence, and on the aggregate bar, which shows no
  // realised value at all.
  realised?: RealisedMarker;
  // Slimmer bar and tighter margins, for the year plan's header strip where the
  // overview card's breathing room is not available.
  isCompact?: boolean;
}

/**
 * Potential against the target ceiling, split into what is locked and what can
 * still be changed, with realised marked as a caret rather than a segment.
 *
 * The bar means one thing only: potential. Realised rides on top so it never
 * competes with the segments for length, which is the encoding chosen on
 * 2026-08-20 over nesting realised inside the locked segment.
 *
 * The track always runs to a fixed multiple of target, so the target rule sits
 * at the same position on every bar (locations stay comparable at a glance) and
 * potential planned above the ceiling has room to draw past it.
 */
export const TargetTrack = ({
  lockedPercent,
  openPercent = 0,
  excessPercent = 0,
  targetLabel,
  realised,
  isCompact = false
}: TargetTrackProps) => {
  const trackStyle = {
    '--target-position': `${TRACK_TARGET_POSITION_PERCENT}%`
  } as CSSProperties;

  return (
    <div
      className={`${styles.track} ${isCompact ? styles.compact : ''}`}
      style={trackStyle}
    >
      {/* Segments live in their own clipped row so the fill keeps the app's
          rounded bar ends, while the rule and caret stay unclipped siblings. */}
      <span className={styles.segments}>
        <span className={styles.locked} style={{ width: `${lockedPercent}%` }} />
        <span className={styles.open} style={{ width: `${openPercent}%` }} />
        {excessPercent > 0 && (
          <span className={styles.excess} style={{ width: `${excessPercent}%` }} />
        )}
      </span>
      <span className={styles.targetFlag}>{targetLabel}</span>
      {realised && (
        // The percent goes in as a bare number so the CSS can use it twice: once
        // to place the caret on the track, once to slide the label by that share
        // of its own width, which is what keeps it inside the track at the ends.
        <span
          className={styles.caret}
          style={{ '--realised-percent': realised.percent } as CSSProperties}
        >
          <span className={styles.caretValue}>{realised.label}</span>
          <span className={styles.caretTip} aria-hidden="true" />
        </span>
      )}
      <span className={styles.targetRule} aria-hidden="true" />
    </div>
  );
};
